// SubAgentActivityItem — nested cognitive/sub-agent activity, collapsed by default.

import { useState, type ReactNode } from 'react';
import type { SubAgentFeed } from './activity-feed';

export interface SubAgentActivityItemProps {
  feed: SubAgentFeed;
  children: ReactNode;
}

function statusLabel(status: SubAgentFeed['status']): string {
  if (status === 'running') return 'Running';
  if (status === 'failed') return 'Failed';
  return 'Done';
}

function statusClass(status: SubAgentFeed['status']): string {
  if (status === 'running') return 'text-amber-600 dark:text-amber-400';
  if (status === 'failed') return 'text-red-600 dark:text-red-400';
  return 'text-green-600 dark:text-green-400';
}

export function SubAgentActivityItem({ feed, children }: SubAgentActivityItemProps) {
  const [open, setOpen] = useState(false);

  return (
    <div
      data-testid="sub-agent-activity"
      data-node-id={feed.nodeId}
      data-expanded={open}
      className="mt-2 border-l-2 border-border/70 pl-3"
    >
      <button
        type="button"
        data-testid="sub-agent-toggle"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-baseline gap-2 py-1 text-left text-sm hover:text-foreground"
      >
        <span
          className={`inline-block shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden
        >
          ▸
        </span>
        <span className="font-medium text-foreground">{feed.label}</span>
        <span className={`text-xs ${statusClass(feed.status)}`}>{statusLabel(feed.status)}</span>
        {feed.fanout && feed.status === 'running' && feed.fanout.done < feed.fanout.total ? (
          <span data-testid="sub-agent-fanout" className="text-xs text-muted-foreground">
            Waiting on {feed.fanout.total - feed.fanout.done} of {feed.fanout.total} subagents
          </span>
        ) : null}
        {feed.summary && !open && (
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{feed.summary}</span>
        )}
      </button>
      {open && (
        <div data-testid="sub-agent-body" className="space-y-1 pb-1">
          {children}
        </div>
      )}
    </div>
  );
}
